import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import type {
  AcquisitionMode,
  BulkPointEditBody,
  ConfigVersion,
  DiscoveredDevice,
  DiscoveryRun,
  EdgeObject,
  EdgeObjects,
  LiveValue,
  PointEditBody,
} from '../api/types.js';
import { useApi } from '../auth.js';
import { Age, ErrorBox, QualityBadge, Spinner } from '../components.js';
import { useDebounced, useLiveValues, useNow, useResource } from '../hooks.js';
import { formatCount, formatPath, siemensPath, formatTimestamp } from '../lib/format.js';
import { newest } from '../lib/live.js';
import { pageInfo } from '../lib/paging.js';
import {
  ACQUISITION_MODES,
  buildConfigBody,
  changeOf,
  deviceHost,
  deviceOptionLabel,
  filterObjects,
  formatCovLive,
  formatCovSupport,
  formatIntervalSeconds,
  hasChanges,
  isAnalogType,
  isStaleSelection,
  NO_EDITS,
  objectKey,
  parseIntervalSeconds,
  pointIdsOf,
  selectionOf,
  setSelected,
  setSettings,
  staleSelectionMessage,
  stateOf,
  needsRemovalConfirm,
  summarize,
  tagsIn,
  typesOf,
  usesInterval,
  valueCell,
  type Edits,
  type SelectionShow,
} from '../lib/selection.js';
import { BulkPointEditor, Limits, PointEditor, TagChips } from '../tags.js';
import { EdgeTabs } from '../protocols.js';

export const PAGE_SIZE = 200;
/** While the edge has not acknowledged a config version yet. */
export const CONFIG_POLL_MS = 3000;
/** While a discovery run is requested or running. */
export const DISCOVERY_POLL_MS = 5000;

const SHOW_LABEL: Record<SelectionShow, string> = {
  all: 'All',
  selected: 'Selected',
  unselected: 'Unselected',
};

const CHANGE_MARK = { added: '+', removed: '−', modified: '~' } as const;

function isActive(run: DiscoveryRun | undefined): boolean {
  return run?.state === 'requested' || run?.state === 'running';
}

/** Header checkbox: all rows on screen selected, some (indeterminate) or none. */
function SelectShown({
  state,
  count,
  onChange,
}: {
  state: 'none' | 'some' | 'all';
  count: number;
  onChange: (selected: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'some';
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={`Transfer all ${String(count)} shown`}
      title={`Select or unselect the ${String(count)} objects shown on this page`}
      checked={state === 'all'}
      onChange={() => {
        onChange(state !== 'all');
      }}
    />
  );
}

function deviceLabel(d: DiscoveredDevice): string {
  const name = d.name ?? d.device_id;
  return d.model ? `${name} · ${d.model}` : name;
}

export function ConfigBadge({ version }: { version: ConfigVersion }) {
  const label =
    version.status === 'sent'
      ? 'Waiting for edge'
      : version.status === 'applied'
        ? 'Applied'
        : 'Rejected';
  return (
    <span className={`badge badge-config-${version.status}`}>
      {version.status === 'sent' && <span className="spinner spinner-small" aria-hidden="true" />}
      {label}
    </span>
  );
}

function ConfigSummary({
  config,
  now,
}: {
  config: { current: ConfigVersion | null; history: ConfigVersion[] };
  now: number;
}) {
  const c = config.current;
  if (!c) return <span className="muted">No configuration sent yet.</span>;
  return (
    <div className="config-summary">
      <div>
        <span className="strong">Config v{c.version}</span> <ConfigBadge version={c} />{' '}
        <span className="muted small">
          {formatCount(c.point_count)} points ·{' '}
          {c.applied_at !== null ? (
            <>
              applied <Age ts={c.applied_at} now={now} />
            </>
          ) : (
            <>
              sent <Age ts={c.sent_at ?? c.created_at} now={now} />
            </>
          )}
        </span>
      </div>
      {c.status === 'rejected' && c.error && <div className="bad small">{c.error}</div>}
      {config.history.length > 1 && (
        <details className="config-history">
          <summary className="muted small">History</summary>
          <table className="table table-compact">
            <tbody>
              {config.history.map((v) => (
                <tr key={v.version}>
                  <td className="mono">v{v.version}</td>
                  <td>
                    <ConfigBadge version={v} />
                  </td>
                  <td className="num">{formatCount(v.point_count)} points</td>
                  <td className="small">{formatTimestamp(v.created_at)}</td>
                  <td className="small bad">{v.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

function DiscoverySummary({
  last,
  latest,
  now,
}: {
  last: DiscoveryRun | null;
  latest: DiscoveryRun | undefined;
  now: number;
}) {
  return (
    <div className="small">
      {isActive(latest) && latest ? (
        <span className="discovery-active">
          <span className="spinner spinner-small" aria-hidden="true" />
          Discovery {latest.state === 'requested' ? 'requested' : 'running'}{' '}
          <Age ts={latest.started_at ?? latest.created_at} now={now} />
        </span>
      ) : last ? (
        <span className="muted">
          Discovered <Age ts={last.finished_at ?? last.created_at} now={now} /> ·{' '}
          {formatCount(last.item_count)} objects
        </span>
      ) : (
        <span className="muted">No discovery yet.</span>
      )}
      {latest?.state === 'failed' && (
        <div className="bad">Last discovery failed: {latest.error ?? 'unknown error'}</div>
      )}
    </div>
  );
}

/** Interval in seconds; commits on blur or Enter, empty means the driver default. */
function IntervalInput({
  ms,
  disabled,
  onCommit,
  label,
}: {
  ms: number | null;
  disabled?: boolean;
  onCommit: (ms: number | null) => void;
  label: string;
}) {
  const [text, setText] = useState(formatIntervalSeconds(ms));
  const [shownMs, setShownMs] = useState(ms);
  if (ms !== shownMs) {
    setShownMs(ms);
    setText(formatIntervalSeconds(ms));
  }
  const parsed = parseIntervalSeconds(text);
  function commit() {
    if (parsed.ok) onCommit(parsed.ms);
    else setText(formatIntervalSeconds(ms));
  }
  return (
    <input
      type="text"
      inputMode="decimal"
      className={`input-interval ${parsed.ok ? '' : 'invalid'}`}
      aria-label={label}
      aria-invalid={!parsed.ok}
      title={parsed.ok ? 'Seconds; empty for the driver default' : parsed.error}
      placeholder="default"
      disabled={disabled}
      value={disabled ? '' : text}
      onChange={(e) => {
        setText(e.target.value);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
      }}
    />
  );
}

/** Live value of a transferred object, else the discovery value marked as such. */
function ValueCell({
  o,
  live,
  scannedAt,
  now,
}: {
  o: EdgeObject;
  live: LiveValue | null | undefined;
  scannedAt: number | null | undefined;
  now: number;
}) {
  const cell = valueCell(o, live, scannedAt);
  if (cell.source === 'scan') {
    return (
      <span className="value-scan" title={cell.title}>
        {cell.text}
        <span className="value-source">scan</span>
      </span>
    );
  }
  return (
    <span className="value-live" title={cell.title}>
      <span className="value">{cell.text}</span>
      {cell.quality && cell.quality !== 'good' && (
        <>
          {' '}
          <QualityBadge q={live?.q} />
        </>
      )}
      {cell.ts !== null && (
        <span className="muted small">
          {' · '}
          <Age ts={cell.ts} now={now} />
        </span>
      )}
    </span>
  );
}

/** Per device: COV support from discovery and the edge driver's live COV states. */
function DevicesPanel({ devices, now }: { devices: DiscoveredDevice[]; now: number }) {
  if (devices.length === 0) return null;
  return (
    <details className="devices-panel">
      <summary className="muted small">Devices and COV ({formatCount(devices.length)})</summary>
      <table className="table table-compact">
        <thead>
          <tr>
            <th>Device</th>
            <th>Address</th>
            <th className="num">Objects</th>
            <th>COV (discovery)</th>
            <th>COV (edge, live)</th>
          </tr>
        </thead>
        <tbody>
          {devices.map((d) => {
            const live = formatCovLive(d);
            return (
              <tr key={d.device_id}>
                <td>
                  {deviceLabel(d)}
                  <div className="muted mono small">{d.device_id}</div>
                </td>
                <td className="mono small">{d.address ?? ''}</td>
                <td className="num">{formatCount(d.object_count)}</td>
                <td className="small">
                  {formatCovSupport(d)}
                  {d.scanned_at != null && (
                    <div className="muted">
                      scanned <Age ts={d.scanned_at} now={now} />
                    </div>
                  )}
                </td>
                <td className="small">
                  {live ?? <span className="muted">not reported</span>}
                  {live && d.cov_live_ts != null && (
                    <div className="muted">
                      <Age ts={d.cov_live_ts} now={now} />
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="muted small">
        Active subscriptions count every subscriber of the device (Kalvio included) at the time of
        the scan; the live column shows what the edge driver holds now.
      </p>
    </details>
  );
}

function ObjectRow({
  o,
  device,
  edits,
  onEdit,
  live,
  now,
  editing,
  saving,
  onEditPoint,
  onSavePoint,
  onPickTag,
}: {
  o: EdgeObject;
  device: DiscoveredDevice | undefined;
  edits: Edits;
  onEdit: (change: (edits: Edits) => Edits) => void;
  live: LiveValue | null | undefined;
  now: number;
  editing: boolean;
  saving: boolean;
  onEditPoint: (open: boolean) => void;
  onSavePoint: (body: PointEditBody) => void;
  onPickTag: (tag: string) => void;
}) {
  const s = stateOf(o, edits);
  const change = changeOf(o, edits);
  return (
    <>
      <tr className={change ? `row-${change}` : undefined}>
        <td className="check">
          <input
            type="checkbox"
            aria-label={`Transfer ${o.object_name ?? o.metric}`}
            checked={s.selected}
            onChange={(e) => {
              const selected = e.target.checked;
              onEdit((ed) => setSelected(ed, [o], selected));
            }}
          />
          {change && (
            <span className={`change-mark change-${change}`} title={`Pending: ${change}`}>
              {CHANGE_MARK[change]}
            </span>
          )}
        </td>
        <td className="mono small" title={device ? deviceOptionLabel(device) : undefined}>
          {device?.name ?? o.device_id}
          {device?.name && <div className="muted">{o.device_id}</div>}
          {device?.address && <div className="muted">{deviceHost(device.address)}</div>}
        </td>
        <td className="mono small">
          {o.object_type}:{o.object_instance}
        </td>
        <td className="mono small">{o.object_name ?? ''}</td>
        <td className="small">
          {o.description ?? ''}
          {siemensPath(o) && (
            <div className="muted" title="Siemens description path (property 3121 / 4397)">
              {formatPath(siemensPath(o), o.description)}
            </div>
          )}
        </td>
        <td className="small tags-cell">
          <TagChips tags={o.tags} onPick={onPickTag} />
          <Limits min={o.min_value} max={o.max_value} units={o.units} />
          {o.point_id && !editing && (
            <button
              type="button"
              className="btn btn-ghost btn-small"
              title="Edit tags and limits"
              onClick={() => {
                onEditPoint(true);
              }}
            >
              Edit
            </button>
          )}
        </td>
        <td className="num">
          <ValueCell o={o} live={live} scannedAt={device?.scanned_at} now={now} />
        </td>
        <td>
          <select
            aria-label="Mode"
            className="select-small"
            value={s.mode}
            onChange={(e) => {
              const mode = e.target.value as AcquisitionMode;
              onEdit((ed) => setSettings(ed, [o], { mode }));
            }}
            disabled={!s.selected}
          >
            {ACQUISITION_MODES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </td>
        <td>
          <IntervalInput
            label="Interval (s)"
            ms={s.interval_ms}
            disabled={!s.selected || !usesInterval(s.mode)}
            onCommit={(interval_ms) => {
              onEdit((ed) => setSettings(ed, [o], { interval_ms }));
            }}
          />
        </td>
        <td>
          {o.point_id && (
            <Link to={`/points/${o.point_id}`} className="small">
              Point
            </Link>
          )}
        </td>
      </tr>
      {editing && o.point_id && (
        <tr className="row-editor">
          <td />
          <td colSpan={9}>
            <PointEditor
              tags={o.tags ?? []}
              min={o.min_value ?? null}
              max={o.max_value ?? null}
              analog={isAnalogType(o.object_type)}
              busy={saving}
              onSave={onSavePoint}
              onCancel={() => {
                onEditPoint(false);
              }}
            />
          </td>
        </tr>
      )}
    </>
  );
}

export function EdgeObjectsPage() {
  const { edgeId = '' } = useParams();
  // Pending edits belong to one edge: start over when it changes.
  return <EdgeObjectsView key={edgeId} edgeId={edgeId} />;
}

function EdgeObjectsView({ edgeId }: { edgeId: string }) {
  const api = useApi();
  const now = useNow(1000);
  const [params, setParams] = useSearchParams();
  const device = params.get('device') ?? '';
  const types = useMemo(() => (params.get('types') ?? '').split(',').filter(Boolean), [params]);
  const q = params.get('q') ?? '';
  const showParam = params.get('show');
  const show: SelectionShow =
    showParam === 'selected' || showParam === 'unselected' ? showParam : 'all';
  const changedOnly = params.get('changed') === '1';
  const tag = params.get('tag') ?? '';
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  // The search box follows the URL (back/forward) and writes to it debounced.
  const [search, setSearch] = useState(q);
  const [shownQ, setShownQ] = useState(q);
  if (q !== shownQ) {
    setShownQ(q);
    setSearch(q);
  }
  const debounced = useDebounced(search, 300);
  const lastDebounced = useRef(debounced);

  function update(next: Record<string, string>, replace = false) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace });
  }

  useEffect(() => {
    if (debounced === lastDebounced.current) return;
    lastDebounced.current = debounced;
    const text = debounced.trim();
    if (text === q) return;
    const p = new URLSearchParams(params);
    if (text) p.set('q', text);
    else p.delete('q');
    p.delete('page');
    setParams(p, { replace: true });
  }, [debounced, q, params, setParams]);

  const [edits, setEdits] = useState<Edits>(NO_EDITS);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<Error | null>(null);
  /** An information message (e.g. after a stale save); cleared by the next action. */
  const [notice, setNotice] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  /** The object whose tags and limits are being edited (`objectKey`). */
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [savingPoints, setSavingPoints] = useState(false);
  // After a save, pending edits are dropped once the reloaded objects arrive.
  const [savedFrom, setSavedFrom] = useState<EdgeObjects | null>(null);
  const [bulkMode, setBulkMode] = useState<AcquisitionMode>('cov+poll');
  /** Mode/interval go to the selected objects on this page or of every page. */
  const [bulkScope, setBulkScope] = useState<'page' | 'matching'>('page');
  const [bulkInterval, setBulkInterval] = useState<number | null>(900_000);

  const objects = useResource(`objects:${edgeId}`, (signal) => api.edgeObjects(edgeId, signal));
  const sites = useResource('sites', () => api.sites());

  // Poll while a discovery run is active or the edge has not acknowledged
  // the newest config version; the flags follow the last response.
  const [pollRuns, setPollRuns] = useState(false);
  const runs = useResource(
    `runs:${edgeId}`,
    (signal) => api.discoveryRuns(edgeId, signal),
    pollRuns ? DISCOVERY_POLL_MS : undefined,
  );
  const currentRun = runs.data?.[0];
  const discovering = isActive(currentRun);
  if (discovering !== pollRuns) setPollRuns(discovering);

  const [pollConfig, setPollConfig] = useState(false);
  const config = useResource(
    `config:${edgeId}`,
    (signal) => api.edgeConfig(edgeId, signal),
    pollConfig ? CONFIG_POLL_MS : undefined,
  );
  const configData = config.data;
  const configPending = configData?.current?.status === 'sent';
  if (configPending !== pollConfig) setPollConfig(configPending);

  // Reload the object list when a run completes that it does not show yet.
  const loaded = objects.data !== undefined;
  const shownRunId = objects.data?.run?.id;
  const reloadObjects = objects.reload;
  useEffect(() => {
    if (loaded && currentRun?.state === 'completed' && shownRunId !== currentRun.id) {
      reloadObjects();
    }
  }, [loaded, currentRun?.state, currentRun?.id, shownRunId, reloadObjects]);

  if (savedFrom && objects.data && objects.data !== savedFrom) {
    setSavedFrom(null);
    setEdits(NO_EDITS);
  }

  const data = objects.data;
  const items = useMemo(() => data?.items ?? [], [data]);
  const devices = useMemo(
    () => new Map((data?.devices ?? []).map((d) => [d.device_id, d])),
    [data],
  );
  const allTypes = useMemo(() => typesOf(items), [items]);
  const matching = useMemo(
    () => filterObjects(items, edits, { device, types, q, show, changedOnly, tag }),
    [items, edits, device, types, q, show, changedOnly, tag],
  );
  const allTags = useMemo(() => tagsIn(items), [items]);
  /** Transferred points among the matching objects: what the bulk tag editor changes. */
  const matchingPoints = useMemo(() => matching.filter((o) => o.point_id !== null), [matching]);
  const matchingAnalog = useMemo(
    () => matchingPoints.filter((o) => isAnalogType(o.object_type)).length,
    [matchingPoints],
  );
  const summary = useMemo(() => summarize(items, edits), [items, edits]);
  const dirty = hasChanges(summary);
  const matchingSelected = useMemo(
    () => matching.filter((o) => stateOf(o, edits).selected).length,
    [matching, edits],
  );

  // Unsaved edits: ask before the tab is closed or reloaded.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
    };
  }, [dirty]);

  const offset = Math.min((page - 1) * PAGE_SIZE, Math.max(0, matching.length - 1));
  const pageStart = Math.floor(offset / PAGE_SIZE) * PAGE_SIZE;
  const rows = matching.slice(pageStart, pageStart + PAGE_SIZE);
  const info = pageInfo(matching.length, pageStart, PAGE_SIZE);

  // Live values of the transferred objects on this page: REST first, then pushed.
  const rowPointIds = useMemo(() => pointIdsOf(rows), [rows]);
  const liveKey = rowPointIds.join(',');
  const seedRes = useResource(`objects-live:${liveKey}`, (signal) =>
    api.liveValues(rowPointIds, signal),
  );
  const seeds = useMemo(() => {
    const byId = new Map((seedRes.data ?? []).map((v) => [v.point_id, v]));
    return rowPointIds.map((id) => byId.get(id) ?? null);
  }, [seedRes.data, rowPointIds]);
  const { live } = useLiveValues(rowPointIds, seeds);
  const pageSelected = useMemo(
    () => rows.filter((o) => stateOf(o, edits).selected).length,
    [rows, edits],
  );
  const bulkSelected = bulkScope === 'page' ? pageSelected : matchingSelected;
  const liveOf = (o: EdgeObject) =>
    o.point_id
      ? newest(seeds[rowPointIds.indexOf(o.point_id)] ?? null, live.values.get(o.point_id))
      : null;

  const site = sites.data?.find((s) => s.id === data?.edge.site_id);
  const edgeName = data?.edge.edge_node_id ?? 'Edge';

  function toggleType(t: string) {
    const next = types.includes(t) ? types.filter((x) => x !== t) : [...types, t];
    update({ types: next.join(','), page: '' });
  }

  async function savePoint(pointId: string, body: PointEditBody) {
    setSavingPoints(true);
    setActionError(null);
    setNotice(null);
    try {
      await api.updatePoint(pointId, body);
      setEditingKey(null);
      objects.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setSavingPoints(false);
    }
  }

  async function applyBulk(body: Omit<BulkPointEditBody, 'point_ids'>) {
    const ids = matchingPoints.flatMap((o) => (o.point_id ? [o.point_id] : []));
    setSavingPoints(true);
    setActionError(null);
    setNotice(null);
    try {
      for (let i = 0; i < ids.length; i += 1000) {
        await api.updatePoints({ ...body, point_ids: ids.slice(i, i + 1000) });
      }
      setNotice(`Tags and limits updated on ${formatCount(ids.length)} points.`);
      objects.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setSavingPoints(false);
    }
  }

  async function save() {
    if (!data) return;
    setNotice(null);
    const body = buildConfigBody(items, edits, data.config_version);
    const sum = summarize(items, edits);
    if (needsRemovalConfirm(sum)) {
      const ok = window.confirm(
        `This stops the transfer of ${formatCount(sum.removed)} of ${formatCount(sum.was)} points` +
          (sum.selected === 0 ? ' — nothing will be transferred from this edge.' : '.') +
          ' Their history stays. Continue?',
      );
      if (!ok) return;
      body.confirm_removal = true;
    }
    setSaving(true);
    setActionError(null);
    try {
      await api.putEdgeConfig(edgeId, body);
      setSavedFrom(data);
      objects.reload();
      config.reload();
    } catch (err) {
      if (isStaleSelection(err)) {
        // Someone else saved meanwhile: show their selection, keep ours on top.
        const pending = sum.added + sum.removed + sum.modified;
        setNotice(staleSelectionMessage(err, pending));
        objects.reload();
        config.reload();
      } else {
        setActionError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      setSaving(false);
    }
  }

  async function startDiscovery() {
    if (
      !window.confirm(
        `Run a BACnet discovery on ${edgeName}? It scans the controllers at a gentle rate and takes several minutes. The current object list and selection stay until it finishes.`,
      )
    ) {
      return;
    }
    setStarting(true);
    setActionError(null);
    try {
      await api.startDiscovery(edgeId);
      runs.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setStarting(false);
    }
  }

  return (
    <section className="has-save-bar">
      <div className="breadcrumbs">
        <Link to="/sites">Sites</Link> <span aria-hidden="true">/</span>{' '}
        {data && (
          <>
            <Link to={`/sites/${data.edge.site_id}`}>{site?.name ?? 'Site'}</Link>{' '}
            <span aria-hidden="true">/</span>
          </>
        )}
      </div>
      <div className="page-head">
        <div>
          <h1>
            BACnet point selection <span className="mono h1-sub">{edgeName}</span>
          </h1>
          <DiscoverySummary last={data?.run ?? null} latest={currentRun} now={now} />
        </div>
        <div className="page-actions">
          <button
            type="button"
            className="btn"
            disabled={discovering || starting || !runs.data}
            onClick={() => {
              void startDiscovery();
            }}
          >
            {discovering ? 'Discovery running…' : 'Run discovery'}
          </button>
        </div>
      </div>
      <EdgeTabs edgeId={edgeId} active="bacnet" />

      <div className="meta">
        {configData ? (
          <ConfigSummary config={configData} now={now} />
        ) : config.error ? (
          <span className="bad">{config.error.message}</span>
        ) : (
          <span className="muted">Loading configuration…</span>
        )}
      </div>

      {notice && (
        <div className="alert alert-info" role="status">
          <span>{notice}</span>
          <button
            type="button"
            className="btn btn-small"
            onClick={() => {
              setNotice(null);
            }}
          >
            Dismiss
          </button>
        </div>
      )}
      {actionError && <ErrorBox error={actionError} />}
      {data && <DevicesPanel devices={data.devices} now={now} />}
      {objects.error && <ErrorBox error={objects.error} onRetry={objects.reload} />}
      {runs.error && <ErrorBox error={runs.error} onRetry={runs.reload} />}
      {!data && objects.loading && <Spinner />}
      {data && items.length === 0 && (
        <p className="empty">
          No discovered objects yet. Run a discovery to list the BACnet objects.
        </p>
      )}

      {items.length > 0 && (
        <>
          <div className="toolbar">
            <input
              type="search"
              className="search"
              placeholder="Search name, description, path, metric (* and ? wildcards)…"
              aria-label="Search objects"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
            <select
              aria-label="Device"
              value={device}
              onChange={(e) => {
                update({ device: e.target.value, page: '' });
              }}
            >
              <option value="">All devices ({formatCount(items.length)})</option>
              {data?.devices.map((d) => (
                <option key={d.device_id} value={d.device_id}>
                  {deviceOptionLabel(d)} ({formatCount(d.object_count)})
                </option>
              ))}
            </select>
            <div className="segmented" role="group" aria-label="Selection">
              {(['all', 'selected', 'unselected'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={show === s ? 'active' : undefined}
                  aria-pressed={show === s}
                  onClick={() => {
                    update({ show: s === 'all' ? '' : s, page: '' });
                  }}
                >
                  {SHOW_LABEL[s]}
                </button>
              ))}
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                checked={changedOnly}
                onChange={(e) => {
                  update({ changed: e.target.checked ? '1' : '', page: '' });
                }}
              />
              Changed only
            </label>
            <select
              aria-label="Tag"
              value={tag}
              onChange={(e) => {
                update({ tag: e.target.value, page: '' });
              }}
            >
              <option value="">All tags</option>
              {tag && !allTags.some(([t]) => t === tag) && <option value={tag}>{tag}</option>}
              {allTags.map(([t, n]) => (
                <option key={t} value={t}>
                  {t} ({formatCount(n)})
                </option>
              ))}
            </select>
          </div>
          <div className="toolbar chips" role="group" aria-label="Object types">
            {allTypes.map((t) => (
              <button
                key={t}
                type="button"
                className={`chip ${types.includes(t) ? 'active' : ''}`}
                aria-pressed={types.includes(t)}
                onClick={() => {
                  toggleType(t);
                }}
              >
                {t}
              </button>
            ))}
            {types.length > 0 && (
              <button
                type="button"
                className="btn btn-ghost btn-small"
                onClick={() => {
                  update({ types: '', page: '' });
                }}
              >
                All types
              </button>
            )}
          </div>

          <div className="toolbar bulk">
            <span className="muted small">
              {formatCount(matching.length)} matching · {formatCount(matchingSelected)} selected
            </span>
            <button
              type="button"
              className="btn btn-small"
              disabled={matchingSelected === matching.length}
              onClick={() => {
                setEdits((ed) => setSelected(ed, matching, true));
              }}
            >
              Select all {formatCount(matching.length)}
            </button>
            <button
              type="button"
              className="btn btn-small"
              disabled={matchingSelected === 0}
              onClick={() => {
                setEdits((ed) => setSelected(ed, matching, false));
              }}
            >
              Unselect all
            </button>
            <span className="bulk-settings">
              <select
                aria-label="Mode for the selected objects"
                className="select-small"
                value={bulkMode}
                onChange={(e) => {
                  setBulkMode(e.target.value as AcquisitionMode);
                }}
              >
                {ACQUISITION_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <IntervalInput
                label="Interval (s) for the selected objects"
                ms={bulkInterval}
                disabled={!usesInterval(bulkMode)}
                onCommit={setBulkInterval}
              />
              <select
                aria-label="Which selected objects get the mode and interval"
                className="select-small"
                value={bulkScope}
                onChange={(e) => {
                  setBulkScope(e.target.value as 'page' | 'matching');
                }}
              >
                <option value="page">on this page</option>
                <option value="matching">on all pages</option>
              </select>
              <button
                type="button"
                className="btn btn-small"
                disabled={bulkSelected === 0}
                onClick={() => {
                  setEdits((ed) =>
                    setSettings(ed, bulkScope === 'page' ? rows : matching, {
                      mode: bulkMode,
                      interval_ms: usesInterval(bulkMode) ? bulkInterval : null,
                    }),
                  );
                }}
              >
                Apply to {formatCount(bulkSelected)} selected
              </button>
            </span>
          </div>
          <div className="toolbar bulk">
            <span
              className="muted small"
              title="Tags and limits belong to points central already has; they apply right away"
            >
              Tags and limits of {formatCount(matchingPoints.length)} transferred matching points:
            </span>
            <BulkPointEditor
              count={matchingPoints.length}
              analogCount={matchingAnalog}
              busy={savingPoints}
              onApply={(body) => {
                void applyBulk(body);
              }}
            />
          </div>

          {matching.length === 0 ? (
            <p className="empty">No objects match.</p>
          ) : (
            <div className="table-wrap">
              <table className="table table-objects">
                <thead>
                  <tr>
                    <th className="check">
                      <SelectShown
                        state={selectionOf(rows, edits)}
                        count={rows.length}
                        onChange={(selected) => {
                          setEdits((ed) => setSelected(ed, rows, selected));
                        }}
                      />
                    </th>
                    <th>Device</th>
                    <th>Object</th>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Tags · limits</th>
                    <th
                      className="num"
                      title="Live value of transferred points; for the others the value read by the discovery scan"
                    >
                      Value
                    </th>
                    <th>Mode</th>
                    <th title="Poll interval for poll, safety poll for cov+poll; empty for the driver default">
                      Interval s
                    </th>
                    <th>
                      <span className="sr-only">Point</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((o) => (
                    <ObjectRow
                      key={objectKey(o)}
                      o={o}
                      device={devices.get(o.device_id)}
                      edits={edits}
                      onEdit={setEdits}
                      live={liveOf(o)}
                      now={now}
                      editing={editingKey === objectKey(o)}
                      saving={savingPoints}
                      onEditPoint={(open) => {
                        setEditingKey(open ? objectKey(o) : null);
                      }}
                      onSavePoint={(body) => {
                        if (o.point_id) void savePoint(o.point_id, body);
                      }}
                      onPickTag={(t) => {
                        update({ tag: t, page: '' });
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {info.total > PAGE_SIZE && (
            <div className="pager">
              <span className="muted">
                {formatCount(info.first)}–{formatCount(info.last)} of {formatCount(info.total)}
              </span>
              <button
                type="button"
                className="btn btn-small"
                disabled={!info.hasPrev}
                onClick={() => {
                  update({ page: info.page > 2 ? String(info.page - 1) : '' });
                }}
              >
                Previous
              </button>
              <span className="muted small">
                Page {info.page} of {info.pages}
              </span>
              <button
                type="button"
                className="btn btn-small"
                disabled={!info.hasNext}
                onClick={() => {
                  update({ page: String(info.page + 1) });
                }}
              >
                Next
              </button>
            </div>
          )}

          <div className="save-bar" role="region" aria-label="Pending changes">
            <span>
              <span className="strong">{formatCount(summary.selected)} selected</span>{' '}
              <span className="muted">(was {formatCount(summary.was)})</span>
              {dirty ? (
                <>
                  {' · '}
                  <span className="change-added">+{formatCount(summary.added)}</span>{' '}
                  <span className="change-removed">−{formatCount(summary.removed)}</span>{' '}
                  <span className="change-modified">~{formatCount(summary.modified)}</span>{' '}
                  <span className="muted">changed</span>
                </>
              ) : (
                configData?.current && (
                  <>
                    {' · '}
                    <span className="muted">v{configData.current.version}</span>{' '}
                    <ConfigBadge version={configData.current} />
                  </>
                )
              )}
            </span>
            <span className="save-bar-actions">
              <button
                type="button"
                className="btn"
                disabled={!dirty || saving}
                onClick={() => {
                  setEdits(NO_EDITS);
                }}
              >
                Discard
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!dirty || saving || savedFrom !== null}
                onClick={() => {
                  void save();
                }}
              >
                {saving ? 'Sending…' : 'Save and send'}
              </button>
            </span>
          </div>
        </>
      )}
    </section>
  );
}
